import { useEffect, useMemo, useState } from "react";
import {
  clientApiService,
  DocumentListItem,
  ProjectItem,
} from "../../services/client-api.service";
import { DocumentViewerModal } from "../../components/documents/DocumentViewerModal";
import { UploadDocumentModal } from "../../components/documents/UploadDocumentModal";

const TYPE_LABELS: Record<string, { label: string; icon: string; className: string }> = {
  plano: { label: "Plano", icon: "📐", className: "bg-blue-100 text-blue-700" },
  contrato: { label: "Contrato", icon: "📋", className: "bg-purple-100 text-purple-700" },
  permiso: { label: "Permiso", icon: "📜", className: "bg-amber-100 text-amber-700" },
  factura: { label: "Factura", icon: "🧾", className: "bg-green-100 text-green-700" },
  reporte: { label: "Reporte", icon: "📊", className: "bg-indigo-100 text-indigo-700" },
  otro: { label: "Otro", icon: "📎", className: "bg-slate-100 text-slate-700" },
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(t: number) {
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function DocumentsListPage() {
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [docs, projs] = await Promise.all([
        clientApiService.listDocuments({
          projectId: projectFilter || undefined,
          type: typeFilter,
          status: statusFilter,
        }),
        clientApiService.listProjects(),
      ]);
      setDocuments(docs);
      setProjects(projs);
    } catch (err: any) {
      setError(err?.message || "Error al cargar documentos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, typeFilter, statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return documents;
    const term = search.trim().toLowerCase();
    return documents.filter(
      (d) =>
        d.name.toLowerCase().includes(term) ||
        d.projectName.toLowerCase().includes(term) ||
        d.category.toLowerCase().includes(term)
    );
  }, [documents, search]);

  const pendingCount = documents.filter(
    (d) => d.requiresSignature && !d.signed
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Documentos</h2>
          <p className="text-sm text-slate-500 mt-1">
            {documents.length} {documents.length === 1 ? "documento" : "documentos"}
            {pendingCount > 0 && (
              <span className="ml-2 text-yellow-600 font-medium">
                • {pendingCount} por firmar
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={isLoading}
            className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            {isLoading ? "..." : "🔄 Actualizar"}
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="text-sm bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            + Subir documento
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Buscar por nombre o proyecto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm"
        />
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los tipos</option>
          <option value="plano">Planos</option>
          <option value="contrato">Contratos</option>
          <option value="permiso">Permisos</option>
          <option value="reporte">Reportes</option>
          <option value="otro">Otros</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los estados</option>
          <option value="pending_signature">Por firmar</option>
          <option value="signed">Firmados</option>
        </select>
      </div>

      {/* Content */}
      {isLoading && documents.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">📄</div>
          <h3 className="text-lg font-semibold text-slate-900">
            {documents.length === 0 ? "Sin documentos aún" : "Sin resultados"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {documents.length === 0
              ? "Aquí verás los planos, contratos, permisos y documentos de tus proyectos."
              : "Prueba con otros filtros."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((doc) => {
            const type = TYPE_LABELS[doc.type] || TYPE_LABELS.otro;
            const pending = doc.requiresSignature && !doc.signed;
            return (
              <button
                key={doc.id}
                onClick={() => setViewingId(doc.id)}
                className={`text-left bg-white rounded-2xl border p-4 hover:shadow-md transition ${
                  pending ? "border-yellow-300" : "border-slate-200 hover:border-primary/40"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${type.className}`}
                  >
                    {type.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate">{doc.name}</p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {doc.projectName}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${type.className}`}>
                        {type.label}
                      </span>
                      {pending && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                          ⏳ Por firmar
                        </span>
                      )}
                      {doc.signed && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          ✅ Firmado
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {formatSize(doc.size)} • {formatDate(doc.createdAt)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal visor */}
      {viewingId && (
        <DocumentViewerModal
          documentId={viewingId}
          onClose={() => setViewingId(null)}
          onSigned={load}
        />
      )}

      {/* Modal subir */}
      {showUpload && (
        <UploadDocumentModal
          projects={projects}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}